class SeparatorAnimations {
  constructor() {
    this.elements = [];
    this.observer = null;
    this.ticking = false;
    this.init();
  }

  init() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.elements = document.querySelectorAll(
      '[data-separator-animate="true"]'
    );

    if (this.elements.length === 0) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      return;
    }

    this.setupObserver();
    this.observeElements();
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.01,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.startTracking(entry.target);
        } else {
          this.stopTracking(entry.target);
        }
      });
    }, options);
  }

  observeElements() {
    this.elements.forEach((element) => {
      this.observer.observe(element);
    });
  }

  scrollHandler = () => {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateVisibleSeparators();
        this.ticking = false;
      });
      this.ticking = true;
    }
  };

  updateVisibleSeparators() {
    this.elements.forEach((element) => {
      if (element.dataset.isTracking === 'true') {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (rect.bottom < 0 || rect.top > windowHeight) {
          if (element.dataset.separatorActive === 'true') {
            this.resetSeparatorState(element);
          }
          return;
        }

        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(windowHeight, rect.bottom);
        const visibleHeight = visibleBottom - visibleTop;
        const ratio = Math.min(1, Math.max(0, visibleHeight / rect.height));

        this.updateSeparatorState(element, ratio);
      }
    });
  }

  startTracking(element) {
    element.dataset.isTracking = 'true';
    if (document.querySelectorAll('[data-is-tracking="true"]').length === 1) {
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  stopTracking(element) {
    element.dataset.isTracking = 'false';
    this.resetSeparatorState(element);
    if (document.querySelectorAll('[data-is-tracking="true"]').length === 0) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }

  updateSeparatorState(element, ratio) {
    const minOpacity = 0.3;
    const maxOpacity = 1.0;
    const minScale = 0.3;
    const maxScale = 1.0;

    const dynamicOpacity = minOpacity + (maxOpacity - minOpacity) * ratio;
    const dynamicScale = minScale + (maxScale - minScale) * ratio;

    element.style.setProperty('--separator-opacity', dynamicOpacity);
    element.style.setProperty('--separator-scale', dynamicScale);

    element.dataset.separatorActive = 'true';
  }

  resetSeparatorState(element) {
    element.dataset.separatorActive = 'false';
    element.style.removeProperty('--separator-height');
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.scrollHandler);
  }
}

class TitleScrollAnimation {
  constructor() {
    this.elements = [];
    this.observer = null;
    this.ticking = false;
    this.init();
  }

  init() {
    this.elements = document.querySelectorAll('[data-title-animate="true"]');
    if (this.elements.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      return;
    }

    this.setupObserver();
    this.observeElements();

    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  setupObserver() {
    const options = { threshold: 0 };
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.dataset.isInview = entry.isIntersecting;
      });
    }, options);
  }

  observeElements() {
    this.elements.forEach((element) => this.observer.observe(element));
  }

  onScroll = () => {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateAnimations();
        this.ticking = false;
      });
      this.ticking = true;
    }
  };

  updateAnimations() {
    this.elements.forEach((element) => {
      if (element.dataset.isInview !== 'true') return;

      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const animationStartPoint = windowHeight * 0.45;
      const animationEndPoint = windowHeight * 0.06;
      const elementBottom = rect.bottom;

      if (elementBottom > animationStartPoint) {
        element.style.setProperty('--title-translate-x', '0px');
        element.style.setProperty('--title-opacity', '1');
        return;
      }

      if (elementBottom <= animationEndPoint) {
        const maxTranslateX = 80;
        const minOpacity = 0.5;
        element.style.setProperty('--title-translate-x', `${maxTranslateX}px`);
        element.style.setProperty('--title-opacity', minOpacity);
        return;
      }

      const totalDistance = animationStartPoint - animationEndPoint;
      const currentDistance = elementBottom - animationEndPoint;
      const ratio = 1 - currentDistance / totalDistance;

      const maxTranslateX = 80;
      const minOpacity = 0.5;

      const currentTranslateX = maxTranslateX * ratio;
      const currentOpacity = 1 - (1 - minOpacity) * ratio;

      element.style.setProperty(
        '--title-translate-x',
        `${currentTranslateX}px`
      );
      element.style.setProperty('--title-opacity', currentOpacity);
    });
  }
}

class VideoScrollAnimation {
  constructor() {
    this.elements = [];
    this.observer = null;
    this.ticking = false;
    this.init();
  }

  init() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.elements = document.querySelectorAll('[data-video-animation]');

    if (this.elements.length === 0) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      return;
    }

    this.setupObserver();
    this.observeElements();
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.01,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.startTracking(entry.target);
        } else {
          this.stopTracking(entry.target);
        }
      });
    }, options);
  }

  observeElements() {
    this.elements.forEach((element) => {
      element.style.opacity = '0.35';
      this.observer.observe(element);
    });
  }

  scrollHandler = () => {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateVisibleVideos();
        this.ticking = false;
      });
      this.ticking = true;
    }
  };

  updateVisibleVideos() {
    this.elements.forEach((element) => {
      if (element.dataset.isVideoTracking === 'true') {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (rect.bottom < 0 || rect.top > windowHeight) {
          if (element.dataset.videoActive === 'true') {
            this.resetVideoState(element);
          }
          return;
        }

        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(windowHeight, rect.bottom);
        const visibleHeight = visibleBottom - visibleTop;
        const ratio = Math.min(1, Math.max(0, visibleHeight / rect.height));

        this.updateVideoState(element, ratio);
      }
    });
  }

  startTracking(element) {
    element.dataset.isVideoTracking = 'true';
    if (
      document.querySelectorAll('[data-is-video-tracking="true"]').length === 1
    ) {
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  stopTracking(element) {
    element.dataset.isVideoTracking = 'false';
    this.resetVideoState(element);
    if (
      document.querySelectorAll('[data-is-video-tracking="true"]').length === 0
    ) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }

  updateVideoState(element, ratio) {
    const minOpacity = 0.35;
    const maxOpacity = 1.0;
    const dynamicOpacity = minOpacity + (maxOpacity - minOpacity) * ratio;

    element.style.opacity = dynamicOpacity;
    element.dataset.videoActive = 'true';
  }

  resetVideoState(element) {
    element.style.opacity = '0.35';
    element.dataset.videoActive = 'false';
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.scrollHandler);
  }
}

class PageTitleAnimation {
  constructor() {
    this.pageBannerTitle = document.querySelectorAll('[data-scroll="true"]');
    this.currentY = 0;
    this.targetY = 0;
    this.easeFactor = 0.05;

    this.scrollHandler = this.scrollHandler.bind(this);
    this.updateAnimation = this.updateAnimation.bind(this);

    this.init();
  }

  init() {
    window.addEventListener('scroll', this.scrollHandler);

    this.updateAnimation();
  }

  scrollHandler() {
    this.targetY = window.scrollY * 0.15;
  }

  updateAnimation() {
    const distance = this.targetY - this.currentY;
    this.currentY += distance * this.easeFactor;

    if (Math.abs(distance) > 0.01) {
      if (window.innerWidth < 1023) {
        this.pageBannerTitle.forEach((banner) => {
          banner.style.transform = `translate(0%, 0%) translate3d(0, -${this.currentY.toFixed(
            2
          )}px, 0)`;
        });
      } else {
        this.pageBannerTitle.forEach((banner) => {
          banner.style.transform = `translate(0%, 0%) translate3d(0, -${this.currentY.toFixed(
            2
          )}px, 0)`;
        });
      }
    }

    requestAnimationFrame(this.updateAnimation);
  }
}

class ShowcaseFadeInAnimation {
  constructor() {
    this.designerBgImage = document.querySelector(
      '#designerShowcase[data-animate="fade"]'
    );
    this.init();
  }

  init() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in');
        } else {
          entry.target.classList.remove('fade-in');
        }
      });
    }, observerOptions);

    if (this.designerBgImage) {
      observer.observe(this.designerBgImage);
    }
  }
}

class PageBanner {
  constructor() {
    this.bannerContainer = document.querySelector('.page-banner');
    this.spaceTitle = document.getElementById('spaceTitle');

    this.init();
  }

  init() {
    this.slider();
  }

  slider() {
    new Swiper(this.bannerContainer, {
      slidesPerView: 1,
      effect: 'fade',
      speed: 1000,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      autoplay: {
        delay: 4000,
      },
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const animatedElements = document.querySelectorAll(
    '[data-text-animate="true"]'
  );

  window.separatorAnimationsInstance = new SeparatorAnimations();
  window.videoAnimationsInstance = new VideoScrollAnimation();
  new TitleScrollAnimation();
  new PageTitleAnimation();
  new ShowcaseFadeInAnimation();

  if (animatedElements.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        } else {
          entry.target.classList.remove('is-visible');
        }
      });
    },
    {
      threshold: 0.1,
    }
  );

  animatedElements.forEach((element) => {
    observer.observe(element);
  });
});

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (window.separatorAnimationsInstance) {
      window.separatorAnimationsInstance.init();
    } else {
      window.separatorAnimationsInstance = new SeparatorAnimations();
    }

    if (window.videoAnimationsInstance) {
      window.videoAnimationsInstance.init();
    } else {
      window.videoAnimationsInstance = new VideoScrollAnimation();
    }
  });
  document.addEventListener('shopify:section:unload', () => {
    if (window.separatorAnimationsInstance) {
      window.separatorAnimationsInstance.destroy();
    }
    if (window.videoAnimationsInstance) {
      window.videoAnimationsInstance.destroy();
    }
  });
}
